import { useMsal } from "@azure/msal-react";
import { useLocalStorage } from "@hooks/useLocalStorage";
import { useNotification } from "@hooks/useNotification";
import { apiBaseUrl, post } from "@services/api.service";
import { AuthContext as AuthContextT } from "@typedef/index";
import axios from "axios";
import React, { createContext, useMemo, useState } from "react";
import { decodeToken } from "react-jwt";
import { useNavigate } from "react-router-dom";
import { deleteCookie } from "@utils/cookieUtils";

export const AuthContext = createContext({} as AuthContextT);

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser, removeUser] = useLocalStorage("user", null);
  const [currentModule, _, removeCurrentModule] = useLocalStorage("currentModule", null);
  const [xAuthProvider, setxAuthProvider] = useLocalStorage("authProvider", "internal");
  const [isLoading, setIsLoading] = useState(false);
  const { instance, accounts } = useMsal();
  const { openToast } = useNotification();

  const navigate = useNavigate();

  const verifyOtp = async (data: any) => {
    // api login here
    setIsLoading(true);
    post("userprofile/verify-otp", data)
      .then((res: any) => {
        const { token, refreshToken } = res.response.data;
        if (token) {
          const tokenDetails: any = decodeToken(token);
          axios
            .get(`${apiBaseUrl}/userprofile/access`, {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            })
            .then(({ data }: any) => {
              if (data.status === "OK") {
                // Set role to SUPER_ADMIN if user has SUPER_ADMIN in access array, otherwise empty string
                const userRole = data.response.data.access?.includes('SUPER_ADMIN') ? 'SUPER_ADMIN' : '';
                setUser({
                  token,
                  refreshToken: refreshToken,
                  email: data.response.data.emailAddress,
                  access: data.response.data.access,
                  currency: data.response.data.currency,
                  role: userRole,
                });
                setxAuthProvider("internal");
                setTimeout(() => {
                  setIsLoading(false);
                  navigate(`/pcd-dashboard`);
                }, 100);
              }
            });
        }
      })
      .catch(() => {
        setIsLoading(false);
      });
  };

  const refreshProfile = async () => {};

  // call this function to sign out logged in user
  const logout = async () => {
    // Clear auth cookies first
    deleteCookie("AUTH_TOKEN");
    
    if (xAuthProvider === "azure" && accounts && accounts[0]) {
      await axios.post(`${apiBaseUrl}/user/logout`, {}, { withCredentials: true });
      let homeAccountId = accounts[0].homeAccountId;
      removeUser("user");
      removeUser("redirecTo");
      removeCurrentModule("currentModule");
      instance.logoutRedirect({
        account: instance.getAccountByHomeId(homeAccountId),
        postLogoutRedirectUri: "/auth/login",
      });
    } else if (xAuthProvider === "local") {
      // Local login logout - call backend to clear cookie
      await axios.post(`${apiBaseUrl}/user/logout`, {}, {
        withCredentials: true,
        headers: {
          "X-Auth-Provider": "local",
        },
      });
      removeUser("user");
      removeUser("redirecTo");
      removeCurrentModule("currentModule");
      window.location.href = "/auth/login";
    } else {
      removeUser("user");
      removeUser("redirecTo");
      removeCurrentModule("currentModule");
      window.location.href = "/auth/login";
    }
  };

  const value: AuthContextT = useMemo(
    () => ({
      user,
      logout,
      refreshProfile,
      isLoading,
      verifyOtp,
    }),
    // eslint-disable-next-line
    [user, isLoading]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
